import {List} from 'immutable';
import React, {useEffect, useRef, useState} from 'react';
import './CallSections.sass';
import {FlagSwallowtail} from '@components/icon/FlagSwallowtail';
import {Seconds} from '@store/@types/util';
import Button from '@material-ui/core/Button';
import formatSecondsShort from 'utils/formatSecondsShort';
import {CallRegion} from '@components/call-card/CallRegion';
import {DragStatus, Section} from '@store/call-card/CallCard.types';
import {PlayerTickGuideId} from '@components/call-card/Player.utils';
import {SetState} from '@store/@types/util/React';
import {TrackHighlight} from '@components/call-card/TrackHighlight';
import {UserComment} from 'records/UserComment';
import {UserId} from 'records/User';
import {SharedAreas} from '@components/call-card/SharedHighlights.utils';
import {DisabledRegionOverlay} from '@components/call-card/DisabledRegionOverlay';
import {FileUploadId} from 'records/CoachingReviewFileUpload';

// Fetch the element at the bottom edge of where our player tick should render
const findTickGuide = () => document.getElementById(PlayerTickGuideId);

interface CallSpeakerOverlaysProps {
  sections: List<Section>;
  duration: Seconds;
  currentTime: Seconds;
  dragStatus: DragStatus;
  setDragStatus: SetState<DragStatus>;
  setNewComment: SetState<UserComment | undefined>;
  userId?: UserId;
  sharedAreas: SharedAreas;
  callId?: FileUploadId;
}

/**
 * Collection of components which span the entire call speaker section.
 */
export const CallSpeakerOverlays: React.FC<CallSpeakerOverlaysProps> = ({
  currentTime,
  duration,
  sections,
  dragStatus,
  setDragStatus,
  setNewComment,
  userId,
  sharedAreas,
  callId,
}) => {
  const callSectionsRef = useRef<HTMLDivElement>(null);
  const [tickHeight, setTickHeight] = useState(0);
  useEffect(() => {
    const obs = new ResizeObserver(() => {
      const top = callSectionsRef.current?.getBoundingClientRect().top;
      const bottom = findTickGuide()?.getBoundingClientRect().bottom;
      if (top && bottom) {
        setTickHeight(bottom - top);
      }
    });
    // We watch this element for changes to recalculate the length of the tick
    // mark when the speaker section is toggled
    obs.observe(document.querySelector('.call-speaker-section')!);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={callSectionsRef} className="call-sections hidden">
      <div className="start">
        <Button size="small">
          <FlagSwallowtail />
          Add
        </Button>
      </div>
      <div className="sections">
        {sections.map(s => (
          <CallRegion
            key={s.name}
            startTime={s.start}
            endTime={s.end}
            color={s.color}
            type="section"
            duration={duration}>
            {s.name}
          </CallRegion>
        ))}
        <PlayerTick duration={duration} currentTime={currentTime} tickHeight={tickHeight} />
        {userId && (
          <TrackHighlight
            tickHeight={tickHeight}
            dragStatus={dragStatus}
            setDragStatus={setDragStatus}
            trackWidth={callSectionsRef.current?.children.item(1)?.clientWidth}
            duration={duration}
            setNewComment={setNewComment}
            userId={userId}
            callId={callId}
          />
        )}
        <DisabledRegionOverlay
          sharedAreas={sharedAreas}
          duration={duration}
          tickHeight={tickHeight}
        />
        <div className="timer" aria-label="Call Progress Value">
          {formatSecondsShort(currentTime)} / {formatSecondsShort(duration)}
        </div>
      </div>
    </div>
  );
};

interface PlayerTickProps {
  tickHeight: number;
  duration: Seconds;
  currentTime: Seconds;
}

function PlayerTick({currentTime, duration, tickHeight}: PlayerTickProps) {
  return (
    <div style={{left: (currentTime / duration) * 100 + '%'}} className="player-tick">
      <div style={{height: tickHeight}} className="longer-part" />
    </div>
  );
}
